import { configureStore, getDefaultMiddleware } from '@reduxjs/toolkit';
import createSagaMiddleware from 'redux-saga';

import { apiSignOut } from 'src/api/account/organizer';
import { apiAccountOUTERSignup, apiAccountSignin } from 'src/api/account/profile';
import { dispatchShowTemplateGalleryModal } from 'src/store/commonPart';
import { commonSlice } from 'src/store/commonPart/slice';
import { dispatchOverlaySliceShow } from 'src/store/overlay/helpers';
import { percentageLoadingMiddleware } from 'src/store/percentageLoadingMiddleware';
import { showPlusSubscriptionModal, showPlanSelectionModal } from 'src/store/plusSubscription/helpers';
import { rootReducers } from 'src/store/rootReducers';
import { currentUserSlice } from 'src/store/user/slice';
import { S3Upload } from 'src/utils/aws-s3';
import { eventPublishAuthChanged } from 'src/utils/outer';

export const sagaMiddleware = createSagaMiddleware({
  onError(err) {
    // eslint-disable-next-line no-console
    console.error(err);
  },
});

export const store = configureStore({
  reducer: rootReducers,
  middleware: [...getDefaultMiddleware({ thunk: false }), ...[sagaMiddleware, percentageLoadingMiddleware]],
  devTools:
    process.env.NODE_ENV === 'development'
      ? {
          name: 'rallyup-devtools',
          trace: true,
        }
      : false,
});

const onSoftLogout = () => {
  eventPublishAuthChanged(null);

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  store.dispatch(currentUserSlice.actions.getCurrentUserOk(null));
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  store.dispatch(commonSlice.actions.setSidebarModel(null));
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  store.dispatch(commonSlice.actions.setGlobalNavigationModel(null));
};

window.spa = {
  signin: (data, cb) => {
    if (data) {
      const callback = cb || Function.prototype;

      apiAccountSignin(data)
        .then((response) => {
          store.dispatch(currentUserSlice.actions.getCurrentUserStart());
          callback(response);
        })
        .catch(callback);
    } else {
      // eslint-disable-next-line no-console
      console.error('data should exist');
    }
  },
  signout: (cb) => {
    const callback = cb || Function.prototype;

    apiSignOut('/signin')
      .then(({ RedirectUrl }) => {
        callback(RedirectUrl);

        onSoftLogout();
      })
      .catch(callback);
  },
  signup: (data, cb) => {
    if (data) {
      const callback = cb || Function.prototype;

      apiAccountOUTERSignup(data)
        .then((response) => {
          store.dispatch(currentUserSlice.actions.getCurrentUserStart());
          callback(response);
        })
        .catch(callback);
    } else {
      // eslint-disable-next-line no-console
      console.error('data should exist');
    }
  },
  onSignout: onSoftLogout,
  showLoginModal: () => null, // declare later (AnywhereLoginModal)
  showTemplateGalleryModal: (calledFrom) => dispatchShowTemplateGalleryModal(calledFrom),

  showExclusiveModal: showPlusSubscriptionModal,
  showPlanSelectionModal,
  showUpgradeNowModal: () => showPlusSubscriptionModal('upgradeNow', { canClose: true }),
  showDonorProfileOverlay: (id) => {
    dispatchOverlaySliceShow({ id, type: 'donorProfile' });
  },
  showCampaignOverlay: (id, noSearchParam) => {
    dispatchOverlaySliceShow({ id, type: 'campaign', noSearchParam });
  },
  uploadFile: (body, mime, fileExtension, fileNamePrefix, successCallback, errorCallback) => {
    S3Upload(body, { contentType: mime, fileNamePrefix, fileExtension })
      .then(({ filePath }) => {
        successCallback(filePath);
      })
      .catch((err) => {
        // aws-sdk v3 an embedded system error (for example, a network error) lies in err.cause
        const originalErrorMsg = err.cause?.message || err.cause?.code || '';

        if (errorCallback) {
          errorCallback({ originalErrorMsg, message: err.message });
        }
      });
  },
};

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
